import React from "react";
import { useParams, Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { LEGAL_DOCS, LEGAL_NAV } from "@/data/legal";
import { COMPANY } from "@/data/site";

function Block({ block }) {
  if (block.type === "ul") {
    return (
      <ul className="space-y-3 my-4">
        {block.items.map((it, i) => (
          <li key={i} className="flex gap-3 text-[#A1A5AB] leading-relaxed">
            <span className="text-[#C8A45A] mt-1.5 shrink-0">—</span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    );
  }
  return <p className="text-[#A1A5AB] leading-relaxed my-4">{block.text}</p>;
}

export default function Legal() {
  const { slug } = useParams();
  const doc = LEGAL_DOCS[slug];

  if (!doc) {
    return (
      <div data-testid="legal-not-found">
        <PageHero overline="Legal" title="Document not found." subtitle="The legal document you are looking for is not available." />
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-24">
          <Link to="/legal/terms" className="text-[#C8A45A] link-underline" data-testid="legal-back">View our legal documents</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`legal-page-${slug}`}>
      <PageHero overline="Legal & Compliance" title={doc.title} subtitle={doc.meta} testId="legal-hero" />

      <section className="py-20 md:py-28 relative z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Sidebar nav */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-28">
              <p className="text-xs uppercase tracking-[0.2em] text-[#737882] mb-5">Documents</p>
              <nav className="flex flex-col gap-1 border-l border-white/10">
                {LEGAL_NAV.map((n) => (
                  <Link
                    key={n.slug}
                    to={`/legal/${n.slug}`}
                    data-testid={`legal-nav-${n.slug}`}
                    className={`pl-4 py-2 text-sm border-l -ml-px transition-colors ${
                      n.slug === slug
                        ? "border-[#C8A45A] text-white"
                        : "border-transparent text-[#A1A5AB] hover:text-white hover:border-white/30"
                    }`}
                  >
                    {n.title}
                  </Link>
                ))}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <div className="lg:col-span-9 max-w-3xl">
            {doc.sections.map((s, i) => (
              <Reveal key={i} delay={Math.min(i * 0.03, 0.2)}>
                <div className="mb-10" data-testid={`legal-section-${i}`}>
                  <h2 className="font-display text-xl md:text-2xl font-medium text-white">{s.heading}</h2>
                  {s.blocks.map((b, j) => (
                    <Block key={j} block={b} />
                  ))}
                </div>
              </Reveal>
            ))}

            <div className="mt-16 pt-8 border-t border-white/10">
              <p className="text-xs text-[#737882] leading-relaxed">
                {COMPANY.legalName} · ACN {COMPANY.acn} · ABN {COMPANY.abn}. {COMPANY.afslStatus} These documents are
                provided for information to wholesale, professional and eligible-counterparty clients and do not
                constitute legal or financial advice.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
